import React, { Context, createContext, PureComponent, ReactNode } from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import uuid from 'uuid/v4';
import { MATOMO_TRACKER_ENABLED, MATOMO_CONSENTLESS_MEDIA_TRACKING_ENABLED } from '../../configs/env';
import { reportError } from '../../error-handling/reportError';
import { withErrorBoundary } from '../../error-handling/withErrorBoundary';
import offlineStore from '../../store/offlineStore';
import { Maybe } from '../../types';
import { logger } from '../../utils/logger';
import { t3RequestOptions } from '../Typo3Provider/config';
import { withUrlLanguage, WithUrlLanguageProps } from '../UrlLanguageProvider/withUrlLanguage';
import { clickedEmailAdress } from './conversions/clickedEmailAdress';
import { clickedPhoneNumber } from './conversions/clickedPhoneNumber';
import { MatomoInstance, MatomoWindow, TrackingEvent } from './types';
import { withUsercentrics, WithUsercentricsProps } from '../UsercentricsProvider/withUsercentrics';
import { Tracker, TrackerCommand, TrackerQueueType } from './Tracker';

const TRACKING_USER_ID_KEY = 'trackingUserId';

const heatmapTrackingEnabled = false;

// TODO/FIXME: Evaluate removing `uuid`

interface PageViewOptions {
  url: string;
  title: string;
  referrerUrl: string;
  storyboardId: string|null;
}

export interface TrackingContextApi {
  trackVehicle: (vehicleId: string) => Promise<void>;
  trackPageView: (pageView: PageViewOptions) => void;
  trackSearchWord: (searchWord: string, countResults: number) => void;
  trackEvents: (events: TrackingEvent[]) => void;
}

type TrackingContextType = Context<TrackingContextApi>;

const defaultContextValue: TrackingContextApi = {
  trackVehicle: async () => {},
  trackPageView: () => {},
  trackSearchWord: () => {},
  trackEvents: () => {},
};

export const TrackingContext: TrackingContextType = createContext(
  defaultContextValue
);

type TrackingProviderProps = WithUrlLanguageProps & RouteComponentProps<any, any, any> & WithUsercentricsProps & {
  children: ReactNode;
};

const getTrackingUserId = async (): Promise<string> => {
  const userId: Maybe<string> = await offlineStore.tryGetItem(
    TRACKING_USER_ID_KEY
  );
  if (userId) {
    return userId;
  }
  const generatedUserId = uuid();
  await offlineStore.trySetItem(TRACKING_USER_ID_KEY, generatedUserId);
  return generatedUserId;
};

declare global {
  interface Window {
    matomoAsyncInit?: () => void;
    Matomo?: MatomoWindow;

    ppiTrackingConfig: {
      visitorType?: string;
      storyboardDimensionId: string | number;

      consented: {
        siteId: string | number;
      };
      consentless: {
        siteId: string | number;
      };
    }
  }
}

const asyncMatomo: Promise<MatomoInstance> = new Promise((res) => {
  if (window.Matomo) {
    res(window.Matomo);
    return;
  }

  window.matomoAsyncInit = () => {
    if (!window.Matomo) {
      logger.error('tracking', 'No Matomo found after matomoAsyncInit');
      return;
    }

    res(window.Matomo);
  };
});

const consentedTracker = new Tracker({
  debugLabel: 'consented',
  awaitMatomo: asyncMatomo,
  url: 'https://www.bus-store.com/piwik/matomo.php',
  siteId: window.ppiTrackingConfig.consented.siteId,
});

const consentlessTracker = new Tracker({
  debugLabel: 'consentless',
  awaitMatomo: asyncMatomo,
  url: 'https://consentless.pluspol-networks.de/matomo.php',
  siteId: window.ppiTrackingConfig.consentless.siteId,
});

consentlessTracker.push([['disableCookies']], 'config');

if (MATOMO_TRACKER_ENABLED) {
  consentlessTracker.enable();
}

function pushTrackingCommands(commands: TrackerCommand[], type?: TrackerQueueType) {
  consentedTracker.push(commands, type);
  consentlessTracker.push(commands, type);
}

export function setHeatmapTracking(enabled: boolean) {
  if (enabled === heatmapTrackingEnabled) {
    return;
  }

  pushTrackingCommands([(tracker) => enabled ? tracker.HeatmapSessionRecording?.enable() : tracker.HeatmapSessionRecording?.disable()], 'config');
}

class TrackingProvider extends PureComponent<TrackingProviderProps> implements TrackingContextApi {
  public trackVehicle = async (vehicleId: string) => {
    const { location } = this.props;
    const userId = await getTrackingUserId();
    const formData = new FormData();
    formData.append('userID', userId);
    formData.append('vin', vehicleId);
    try {
      const fetchResponse = await fetch(
        `${location.pathname}?eID=ppi_busstore_bustracking`,
        { ...t3RequestOptions, body: formData, method: 'POST' }
      );
      if (!fetchResponse.ok) {
        throw new Error(
          `TrackingProvider.trackVehicle: ${fetchResponse.statusText} (${
            fetchResponse.status
          })`
        );
      }
    } catch (e) {
      reportError('TrackingProvider.trackVehicle()', e);
    }
  };

  public trackPageView = ({
    url,
    title,
    referrerUrl,
    storyboardId
  }: PageViewOptions) => {
    if (!MATOMO_TRACKER_ENABLED) {
      return;
    }

    let pageViewTrackings = [];

    if (storyboardId) {
      pageViewTrackings.push(['setCustomDimension', window.ppiTrackingConfig.storyboardDimensionId, storyboardId])
    }

    pageViewTrackings = [
      ...pageViewTrackings,
      ['setCustomUrl', url],
      ['setDocumentTitle', title],
      ['setReferrerUrl', referrerUrl],
      ['trackPageView'],
    ];

    if (storyboardId) {
      pageViewTrackings.push(['deleteCustomDimension', window.ppiTrackingConfig.storyboardDimensionId]);
    }

    pushTrackingCommands(pageViewTrackings, 'pageview');
  };

  public trackSearchWord = (searchWord: string, countResults: number) => {
    if (!MATOMO_TRACKER_ENABLED) {
      return;
    }

    pushTrackingCommands([['trackSiteSearch', searchWord, false, countResults]]);
  };

  public trackEvents = (events: TrackingEvent[]) => {
    if (!MATOMO_TRACKER_ENABLED) {
      return;
    }

    pushTrackingCommands(events
      .map((event) => {
        const data: TrackerCommand = ['trackEvent', event.category, event.action];
        if (event.name !== undefined) {
          data.push(event.name);

          if (event.value !== undefined) {
            data.push(event.value);
          }
        }

        return data;
      }))
    ;
  }

  private onDomClick = (event: MouseEvent) => {
    const link = event.target instanceof HTMLAnchorElement ? event.target : undefined;

    if (this.props.usercentrics.matomoEnabled || MATOMO_CONSENTLESS_MEDIA_TRACKING_ENABLED) {
      window.Matomo?.MediaAnalytics.scanForMedia();
    }

    if (!link) {
      return;
    }

    const {href} = link;

    if (href.startsWith('tel:')) {
      this.trackEvents(clickedPhoneNumber(link.innerText));
    } else if (href.startsWith('mailto:')) {
      this.trackEvents(clickedEmailAdress(link.innerText));
    }
  };

  componentDidMount() {
    document.body.addEventListener('click', this.onDomClick);

    window.Matomo?.addPlugin('TrackerVisitorIdCookie0', {}); //see comment in componentDidUpdate

    if (window.ppiTrackingConfig.visitorType) {
      pushTrackingCommands([['setCustomVariable', 1, window.ppiTrackingConfig.visitorType, '1', 'visit']], 'config');
    }

    pushTrackingCommands([
      ['enableLinkTracking'],
      (tracker) => tracker.HeatmapSessionRecording?.disable(),
    ], 'config');

    if (this.props.usercentrics.matomoEnabled && MATOMO_TRACKER_ENABLED) {
      consentedTracker.enable();
      this.setMatomoMediaTrackers(true);
    } else {
      this.setMatomoMediaTrackers(false);
    }
  }

  componentDidUpdate(prevProps: Readonly<TrackingProviderProps>): void {

    window.Matomo?.addPlugin('TrackerVisitorIdCookie0', {}); //Hack: need to overwrite Plugin or else consentless tracker will set a cookie on reload without consent

    if (this.props.usercentrics.matomoEnabled && !prevProps.usercentrics.matomoEnabled && MATOMO_TRACKER_ENABLED) {
      consentedTracker.enable();
      this.setMatomoMediaTrackers(true);
    }
  }

  componentWillUnmount() {
    document.body.removeEventListener('click', this.onDomClick);
  }

  private setMatomoMediaTrackers(hasConsent: boolean) {
    if (!hasConsent && !MATOMO_CONSENTLESS_MEDIA_TRACKING_ENABLED) {
      return;
    }
    if (window.Matomo) {
      let mediaTrackers = [];
      if (MATOMO_CONSENTLESS_MEDIA_TRACKING_ENABLED) {
        mediaTrackers.push(consentlessTracker.getTracker());
      }
      if (hasConsent) {
        mediaTrackers.push(consentedTracker.getTracker());
      }
      if (window.Matomo.MediaAnalytics) {
        window.Matomo.MediaAnalytics.setMatomoTrackers(mediaTrackers);
      }
    }
  }

  public render() {
    const { children } = this.props;
    return (
      <TrackingContext.Provider value={this}>
        {children}
      </TrackingContext.Provider>
    );
  }
}

export default withErrorBoundary(
  withRouter(withUrlLanguage(withUsercentrics(TrackingProvider))),
  'TrackingProvider'
);
